"use client"
import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import Text from "@/app/(site)/_components/Atoms/Text"
import { Badge } from "@/app/(site)/_components/Molecules/Badge"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { useProductDisplayInfo } from "@/app/(site)/_components/Organisms/Cards/ProductCards/helper"
import { debounce } from "@/app/(site)/_utils/debounce"
import { OLDgetImageSrc } from "@/app/(site)/_utils/getImageSrc"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { selectItemEvent } from "@/lib/utils/tracking/gtmEvents"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { t } from "@/translations"
import {
    ImageSize,
    MinimizedCentraProduct,
    MinimizedMedia,
    MinimizedRelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useState } from "react"

type RegularProductCardProps = {
    productVariants: MinimizedCentraProduct[]
    lazyLoad?: boolean
    listName?: string
    listId?: string
    listType?: string
    listHierarchy?: string
    index?: number
}

const RegularProductCard = ({
    productVariants,
    lazyLoad = false,
    listName = "Product List",
    listId = "plp",
    listType = "plp",
    listHierarchy,
    index
}: RegularProductCardProps) => {
    const langCode = getLangCode(usePathname().split("/")[1]) ?? ""
    const siteContext = useSiteContext()
    const {
        setCurrentVariant,
        uri,
        badges,
        colorVariantsAmount,
        sortedColor,
        name,
        extraColorsCount,
        price,
        priceBeforeDiscount,
        anyVariantisAvailable,
        imageSources,
        showAsOnSale,
        showAsNew,
        currentProduct
    } = useProductDisplayInfo(productVariants)

    // Initialize image sources (ensure imageSources exists)
    const initialImageSrc =
        typeof window !== "undefined"
            ? OLDgetImageSrc(window.innerWidth, imageSources)
            : OLDgetImageSrc(1440, imageSources) // Fallback for SSR

    const initialHoverImageSrc =
        typeof window !== "undefined" && window.innerWidth <= 1440
            ? imageSources?.small?.[1]
            : imageSources?.standard?.[1]

    const [imageSrc, setImageSrc] = useState<string>(initialImageSrc)
    const [hoverImageSrc, setHoverImageSrc] = useState<string>(initialHoverImageSrc)
    const [currentBadges, setCurrentBadges] = useState<string[]>(badges || [])

    const handleResize = useCallback(() => {
        if (imageSources) {
            setImageSrc(OLDgetImageSrc(window.innerWidth, imageSources))
            setHoverImageSrc(
                window.innerWidth <= 1440
                    ? imageSources?.small?.[1]
                    : imageSources?.standard?.[1]
            )
        }
    }, [imageSources])

    useEffect(() => {
        const debouncedHandleResize = debounce(handleResize, 250)

        window.addEventListener("resize", debouncedHandleResize)

        return () => {
            window.removeEventListener("resize", debouncedHandleResize)
        }
    }, [handleResize, imageSources])

    const handleMouseEnter = (hoverImageSrc: string, productBadges: string[]) => {
        setImageSrc(hoverImageSrc ? hoverImageSrc : imageSrc)
        setCurrentBadges(productBadges || [])
    }

    const handleMouseLeave = (
        imageSources: {
            mini: ImageSize[]
            small: ImageSize[]
            standard: ImageSize[]
            big: ImageSize[]
            full: ImageSize[]
        },
        originalBadges: string[]
    ) => {
        setImageSrc(OLDgetImageSrc(window.innerWidth, imageSources))
        setCurrentBadges(originalBadges || currentBadges)
    }
    const handleChangeVariant = useCallback(
        (product: MinimizedCentraProduct | MinimizedRelatedProduct) => {
            const newImageSources: MinimizedMedia = product?.mediaObjects

            setImageSrc(OLDgetImageSrc(window.innerWidth, newImageSources))

            setCurrentVariant({
                imageSources: newImageSources,
                name: product.name,
                price: product.price,
                priceBeforeDiscount: product.priceBeforeDiscount,
                available: product.available,
                uri: product.uri,
                showAsOnSale: product.showAsOnSale,
                showAsNew: product.showAsNew
            })

            setCurrentBadges([
                ...(product.showAsNew ? ["NEW"] : []),
                ...(product.showAsOnSale ? ["SALE"] : []),
                ...(product.badge || [])
            ])
        },
        [setCurrentVariant]
    )

    useEffect(() => {
        setCurrentBadges([
            ...(showAsNew ? ["NEW"] : []),
            ...(showAsOnSale ? ["SALE"] : []),
            ...(badges || [])
        ])
    }, [showAsNew, showAsOnSale, badges])

    const handleProductClick = () => {
        // Find the product variant that matches the current display
        const productToTrack =
            productVariants.find(
                p =>
                    p.name === currentProduct?.name ||
                    p.color_swatch?.color_name ===
                        currentProduct?.color_swatch?.color_name
            ) || productVariants[0]

        if (productToTrack) {
            selectItemEvent(
                productToTrack,
                { size: undefined, index }, // Pass index through item parameter
                listName,
                listId,
                siteContext,
                listType,
                listHierarchy
            )
        }
    }

    return (
        <div
            className="group relative"
            data-search-result-id={productVariants?.[0]?.search_result_id}
            data-product-listing-result-id={
                productVariants?.[0]?.product_listing_result_id
            }
            data-recommendation-id={productVariants?.[0]?.recommendation_id}
        >
            <DynamicLink href={uri} prefetch={false}>
                <div className="cursor-pointer" onClick={handleProductClick}>
                    <div className="bg-[#f4f5f5]">
                        {Array.isArray(currentBadges) && currentBadges.length > 0 && (
                            <div className="absolute left-0 right-0 top-0 z-[5] flex flex-wrap gap-2">
                                {currentBadges.map((badge, index) => (
                                    <Badge key={index} badge={badge} />
                                ))}
                            </div>
                        )}
                        <div className="relative mb-16 aspect-square overflow-hidden">
                            <BaseImage
                                fill
                                key={imageSrc}
                                loading={lazyLoad ? "lazy" : "eager"}
                                decoding={lazyLoad ? "async" : "sync"}
                                src={imageSrc}
                                alt={name}
                                className="object-contain"
                                onMouseEnter={() =>
                                    hoverImageSrc &&
                                    handleMouseEnter(hoverImageSrc, currentBadges)
                                }
                                onMouseLeave={() =>
                                    handleMouseLeave(imageSources, currentBadges)
                                }
                            />
                        </div>
                    </div>
                    <div className="w-full">
                        <Text size="small" weight="regular" className="md:text-16">
                            {name}
                        </Text>
                        <Text
                            size="small"
                            weight="regular"
                            className="flex flex-wrap gap-x-8 break-words pb-8 text-main-dark-grey md:text-16"
                        >
                            <span>{price}</span>
                            {showAsOnSale && (
                                <span className="line-through">
                                    {priceBeforeDiscount}
                                </span>
                            )}
                        </Text>
                    </div>
                </div>
            </DynamicLink>
            <div className="flex h-38 w-full">
                <div className="hidden md:group-hover:block">
                    <div className="flex w-full items-center">
                        {sortedColor.map((swatch, index) => (
                            <DynamicLink
                                href={`/product/${swatch.uri}`}
                                className="cursor-pointer"
                                onMouseEnter={() => handleChangeVariant(swatch.product)}
                                onMouseLeave={() =>
                                    handleChangeVariant(productVariants[0])
                                }
                                onClick={() => {
                                    selectItemEvent(
                                        swatch.product,
                                        { size: undefined, index: index },
                                        listName,
                                        listId,
                                        siteContext,
                                        listType,
                                        listHierarchy
                                    )
                                }}
                                key={index}
                                prefetch={false}
                            >
                                {swatch.color ? (
                                    <div className="m-4 h-24 w-24 rotate-[-50deg]">
                                        <div className="relative h-24 w-24">
                                            {swatch.image ? (
                                                <Image
                                                    alt={swatch.name}
                                                    src={swatch.image.url}
                                                    width={100}
                                                    height={100}
                                                    className="absolute inset-0 z-20 rounded-full"
                                                    style={{
                                                        clipPath:
                                                            "polygon(0 0, 50% 0, 50% 100%, 0 100%)"
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="absolute inset-0 rounded-full"
                                                    style={{
                                                        clipPath:
                                                            "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                        backgroundColor:
                                                            swatch.lens?.hex ??
                                                            swatch.color
                                                    }}
                                                ></div>
                                            )}
                                            <div
                                                className="absolute inset-0 rounded-full"
                                                style={{
                                                    clipPath:
                                                        "polygon(50% 0, 100% 0, 100% 100%, 50% 100%)",
                                                    backgroundColor: swatch.color
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                ) : null}
                            </DynamicLink>
                        ))}
                        {extraColorsCount > 0 && (
                            <div>
                                <Text
                                    size="small"
                                    weight="regular"
                                    className="text-main-dark-grey md:text-16"
                                >
                                    +{extraColorsCount} {t(langCode, "common.more")}
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
                {colorVariantsAmount > 0 && (
                    <div>
                        <Text
                            size="small"
                            weight="regular"
                            className="text-main-dark-grey md:text-16 md:group-hover:hidden"
                        >
                            {colorVariantsAmount} {t(langCode, "product_page.colors")}
                        </Text>
                    </div>
                )}
            </div>
            {!anyVariantisAvailable && (
                <Text
                    size="small"
                    weight="regular"
                    className="text-main-black md:text-16"
                >
                    {t(langCode, "product_page.out_of_stock")}
                </Text>
            )}
        </div>
    )
}

export default RegularProductCard
